import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  ActivityIndicator,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from 'react-native';
import OneClickBookingScreen from './OneClickBookingScreen';
import {
  buildWidgetEmbedBookingCompletePayload,
  getWidgetBookingParamsFromUrl,
  isInIframe,
  navigateToWebAppAfterWidgetBooking,
  notifyWidgetEmbedParent,
  WIDGET_BOOKING_API,
} from '../utils/widgetEmbed';
import type { InstantBooking } from '../services/instantBookingService';
import { UnifiedAuthService } from '../services/unifiedAuthService';

const WidgetEmbedBookingScreen: React.FC = () => {
  const params = getWidgetBookingParamsFromUrl();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [business, setBusiness] = useState<any>(null);
  const [authUser, setAuthUser] = useState<any>(null);
  const [completedBooking, setCompletedBooking] = useState<InstantBooking | null>(null);

  useEffect(() => {
    UnifiedAuthService.getCurrentUser()
      .then((u) => {
        if (u) {
          setAuthUser(u);
        }
      })
      .catch(() => {
        /* guest flow */
      });
  }, []);

  useEffect(() => {
    if (!params?.businessId) {
      setError('Missing business for booking.');
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${WIDGET_BOOKING_API}/business/${encodeURIComponent(params.businessId)}`,
        );
        const data = await res.json();
        if (cancelled) {
          return;
        }
        if (!res.ok || !data?.success) {
          setError('Could not load this business. Try again later.');
          return;
        }
        const businessCategory =
          data.categoryId || data.industry || data.category || 'services';
        setBusiness({
          id: data.id,
          name: data.businessName || 'Business',
          category: businessCategory,
          type: businessCategory,
          rating: 0,
          distance: '',
          address: data.address || '',
          phone: data.phone || '',
          email: data.email || '',
          services: [],
          isOpen: true,
          priceRange: '',
          hours: '',
          isFavorite: false,
          isOnBookAPT: true,
          businessHours: data.businessHours || undefined,
          appointmentDuration: data.appointmentDuration ?? undefined,
          paymentRequiredAtBooking: data.paymentRequiredAtBooking === true,
        });
      } catch {
        if (!cancelled) {
          setError('Could not load booking. Check your connection.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [params?.businessId]);

  const handleBookingComplete = async (booking: InstantBooking) => {
    notifyWidgetEmbedParent(
      'booking-complete',
      buildWidgetEmbedBookingCompletePayload(booking),
    );

    // Merchant iframe: parent widget closes the overlay — no in-app navigation.
    if (isInIframe()) {
      return;
    }

    setCompletedBooking(booking);
  };

  const handleViewAppointments = () => {
    navigateToWebAppAfterWidgetBooking();
  };

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color="#667eea" />
        <Text style={styles.loadingText}>Loading booking...</Text>
      </View>
    );
  }

  if (error || !business || !params) {
    return (
      <View style={styles.center}>
        <Text style={styles.errorText}>{error || 'Unable to start booking.'}</Text>
      </View>
    );
  }

  if (completedBooking) {
    const emailSentAt = new Date().toLocaleString();
    const isDeliveryOrder = completedBooking.serviceName === 'Delivery order';
    return (
      <View style={styles.center}>
        <Text style={styles.successTitle}>
          {isDeliveryOrder ? 'Delivery order confirmed' : 'Booking confirmed'}
        </Text>
        <Text style={styles.successText}>
          {isDeliveryOrder
            ? `Your order is confirmed${completedBooking.id ? ` (#${String(completedBooking.id).slice(-8)})` : ''}. We'll notify you when your driver is assigned — tracking updates come by text or email.`
            : `Your ${completedBooking.serviceName} appointment is set for ${completedBooking.appointmentDate} at ${completedBooking.appointmentTime}.`}
        </Text>
        {completedBooking.customerData?.email ? (
          <Text style={styles.emailSentText}>
            A confirmation email has been sent to {completedBooking.customerData.email} at{' '}
            {emailSentAt}.
          </Text>
        ) : null}
        {Platform.OS === 'web' ? (
          <View style={styles.successActions}>
            <TouchableOpacity style={styles.primaryButton} onPress={handleViewAppointments}>
              <Text style={styles.primaryButtonText}>View my booking at BookAPT</Text>
            </TouchableOpacity>
          </View>
        ) : null}
      </View>
    );
  }

  const userId = authUser?.id || authUser?.uid || 'guest_embed';

  return (
    <OneClickBookingScreen
      guestMode
      onBack={() => notifyWidgetEmbedParent('close')}
      userId={userId}
      user={authUser || undefined}
      onGuestAuthenticated={setAuthUser}
      onBookingComplete={handleBookingComplete}
      preSelectedBusiness={business}
      preSelectedServiceId={params.serviceId}
    />
  );
};

const styles = StyleSheet.create({
  center: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
    backgroundColor: '#fff',
  },
  loadingText: {
    marginTop: 12,
    color: '#64748b',
    fontSize: 15,
  },
  errorText: {
    color: '#dc3545',
    fontSize: 15,
    textAlign: 'center',
  },
  successTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: '#0f172a',
    marginBottom: 12,
    textAlign: 'center',
  },
  successText: {
    fontSize: 15,
    color: '#475569',
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 12,
  },
  emailSentText: {
    fontSize: 14,
    color: '#64748b',
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 24,
  },
  successActions: {
    alignSelf: 'stretch',
    alignItems: 'flex-end',
    width: '100%',
    maxWidth: 360,
  },
  primaryButton: {
    backgroundColor: '#667eea',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 10,
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
  },
});

export default WidgetEmbedBookingScreen;
